import React, { useRef, useEffect, useState } from "react";

/**
 * Lightweight IntersectionObserver-driven reveal. No framer-motion needed.
 * Variants: "slide-up" | "slide-right" | "slide-left" | "fade" | "zoom" | "tilt" | "stagger"
 */
export default function Reveal({ children, variant = "slide-up", delay = 0, className = "", stagger = false, as: Tag = "div", once = true, threshold = 0.05 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) io.unobserve(e.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px 15% 0px" }
    );
    io.observe(el);
    // Safety net ONLY for elements that are already in the viewport on mount
    // (e.g. above-the-fold content where the initial observer callback might race).
    // Below-fold elements keep their pristine start state so scroll triggers the animation.
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    const alreadyInView = rect.top < vh && rect.bottom > 0;
    let safety;
    if (alreadyInView) {
      safety = setTimeout(() => setVisible(true), 800);
    }
    return () => { if (safety) clearTimeout(safety); io.disconnect(); };
  }, [once, threshold]);

  const cls = `reveal reveal-${variant}${visible ? " is-visible" : ""}${stagger ? " reveal-stagger" : ""} ${className}`;
  const style = delay ? { transitionDelay: `${delay}ms` } : undefined;

  return <Tag ref={ref} className={cls} style={style}>{children}</Tag>;
}
